import type { AttributionData } from "@/lib/analytics/utm-tracker";
import {
  ONBOARDING_SIGNUP_TIMING_QUERY_KEY,
  type OnboardingSignupTimingVariant,
  parseOnboardingSignupTimingVariant,
} from "@/lib/experiments/onboardingSignupTiming";
import type { PackageType } from "@/lib/revenuecat/config";
import type { OnboardingState, QuizAnswers } from "./types";

export const ONBOARDING_STEP_IDS = [
  "language",
  "motivation",
  "proficiency",
  "speaking-intro",
  "speaking-demo",
  "trial",
] as const;

export type OnboardingStepId = (typeof ONBOARDING_STEP_IDS)[number];

const MOTIVATION_COPY: Record<string, { label: string; challenge: string }> = {
  travel: {
    label: "Travel",
    challenge: "Say hello, then order something you would enjoy.",
  },
  career: {
    label: "Work",
    challenge: "Introduce yourself and say what you do.",
  },
  family: {
    label: "Family and friends",
    challenge: "Say hello and ask one friendly question.",
  },
  culture: {
    label: "Films, music, and culture",
    challenge: "Share a film, song, or food you enjoy.",
  },
  education: {
    label: "Study",
    challenge: "Introduce yourself and say what you are studying.",
  },
  personal: {
    label: "A personal goal",
    challenge: "Say hello and share why you chose this language.",
  },
};

const LEVEL_COPY: Record<string, string> = {
  A0: "Starting from zero",
  A1: "A few words and phrases",
  A2: "Simple exchanges",
  B1: "Everyday conversations",
  B2: "Confident conversations",
  C1: "Advanced conversation",
};

export interface OnboardingPlanSummary {
  motivationLabel: string;
  levelLabel: string;
  firstChallenge: string;
}

interface OnboardingTrialSelection {
  packageType?: PackageType | null;
  coupon?: string | null;
  experimentVariant?: OnboardingSignupTimingVariant | null;
}

interface OnboardingFreePracticeOptions {
  oauthSignup?: boolean;
}

const ONBOARDING_STATE_MAX_AGE_MS = 60 * 60 * 1000;
const ONBOARDING_TRIAL_RESUME_COUPON_MAX_LENGTH = 128;
const ONBOARDING_TRIAL_RESUME_KEYS = new Set([
  "resume",
  "plan",
  "coupon",
  ONBOARDING_SIGNUP_TIMING_QUERY_KEY,
]);
const ONBOARDING_FREE_RESUME_KEYS = new Set([
  "resume",
  "language",
  "language_name",
  "level",
  "motivation",
  ONBOARDING_SIGNUP_TIMING_QUERY_KEY,
]);
const ONBOARDING_LEVEL_TO_RUNTIME_LEVEL: Record<string, string> = {
  A0: "absolute_beginner",
  A1: "beginner",
  A2: "beginner",
  B1: "intermediate",
  B2: "intermediate",
  C1: "advanced",
};

function getSearchParams(
  search: string | Pick<URLSearchParams, "get">
): Pick<URLSearchParams, "get"> {
  return typeof search === "string" ? new URLSearchParams(search) : search;
}

function getBoundedSearchValue(
  search: Pick<URLSearchParams, "get">,
  key: string,
  maxLength: number
): string | null {
  const value = search.get(key)?.trim();
  return value && value.length <= maxLength ? value : null;
}

export function isOnboardingTrialResume(search: string | Pick<URLSearchParams, "get">): boolean {
  return getSearchParams(search).get("resume") === "trial";
}

export function isOnboardingDemoResume(search: string | Pick<URLSearchParams, "get">): boolean {
  return getSearchParams(search).get("resume") === "demo";
}

export function isOnboardingFreeResume(search: string | Pick<URLSearchParams, "get">): boolean {
  return getSearchParams(search).get("resume") === "free";
}

export function getOnboardingResumePackage(
  search: string | Pick<URLSearchParams, "get">
): PackageType | null {
  if (!isOnboardingTrialResume(search)) return null;

  const packageType = getSearchParams(search).get("plan");
  return packageType === "weekly" || packageType === "monthly" || packageType === "annual"
    ? packageType
    : null;
}

export function isOnboardingTrialResumePath(value: string | null | undefined): boolean {
  if (!value?.startsWith("/") || value.startsWith("//")) return false;

  try {
    const url = new URL(value, "https://www.chickytutor.com");
    if (url.origin !== "https://www.chickytutor.com" || url.pathname !== "/onboarding") {
      return false;
    }
    if (!isOnboardingTrialResume(url.searchParams)) return false;
    if ([...url.searchParams.keys()].some((key) => !ONBOARDING_TRIAL_RESUME_KEYS.has(key))) {
      return false;
    }
    if ([...ONBOARDING_TRIAL_RESUME_KEYS].some((key) => url.searchParams.getAll(key).length > 1)) {
      return false;
    }

    const packageType = url.searchParams.get("plan");
    if (packageType !== null && getOnboardingResumePackage(url.searchParams) === null) {
      return false;
    }

    const coupon = url.searchParams.get("coupon");
    if (
      coupon !== null &&
      (!coupon.trim() || coupon.trim().length > ONBOARDING_TRIAL_RESUME_COUPON_MAX_LENGTH)
    ) {
      return false;
    }

    const experimentVariant = url.searchParams.get(ONBOARDING_SIGNUP_TIMING_QUERY_KEY);
    return (
      experimentVariant === null || parseOnboardingSignupTimingVariant(experimentVariant) !== null
    );
  } catch {
    return false;
  }
}

export function isOnboardingFreeResumePath(value: string | null | undefined): boolean {
  if (!value?.startsWith("/") || value.startsWith("//")) return false;

  try {
    const url = new URL(value, "https://www.chickytutor.com");
    if (url.origin !== "https://www.chickytutor.com" || url.pathname !== "/onboarding") {
      return false;
    }
    if (!isOnboardingFreeResume(url.searchParams)) return false;
    if ([...url.searchParams.keys()].some((key) => !ONBOARDING_FREE_RESUME_KEYS.has(key))) {
      return false;
    }
    if ([...ONBOARDING_FREE_RESUME_KEYS].some((key) => url.searchParams.getAll(key).length > 1)) {
      return false;
    }

    const language = getBoundedSearchValue(url.searchParams, "language", 32);
    if (!language || !/^[A-Za-z0-9-]+$/.test(language)) return false;

    const languageName = url.searchParams.get("language_name");
    if (languageName !== null && !getBoundedSearchValue(url.searchParams, "language_name", 80)) {
      return false;
    }

    const proficiencyLevel = url.searchParams.get("level");
    if (proficiencyLevel !== null && !(proficiencyLevel in LEVEL_COPY)) return false;

    const motivation = url.searchParams.get("motivation");
    if (motivation !== null && !(motivation in MOTIVATION_COPY)) return false;

    const experimentVariant = url.searchParams.get(ONBOARDING_SIGNUP_TIMING_QUERY_KEY);
    return (
      experimentVariant === null || parseOnboardingSignupTimingVariant(experimentVariant) !== null
    );
  } catch {
    return false;
  }
}

function restoreBoundedOnboardingAnswers(
  answers: QuizAnswers,
  searchParams: Pick<URLSearchParams, "get">
): QuizAnswers {
  const restoredAnswers = { ...answers };
  const language = getBoundedSearchValue(searchParams, "language", 32);
  const languageName = getBoundedSearchValue(searchParams, "language_name", 80);
  const proficiencyLevel = getBoundedSearchValue(searchParams, "level", 8);
  const motivation = getBoundedSearchValue(searchParams, "motivation", 32);

  if (language && /^[A-Za-z0-9-]+$/.test(language)) restoredAnswers.language = language;
  if (languageName) restoredAnswers.languageName = languageName;
  if (proficiencyLevel && proficiencyLevel in LEVEL_COPY) {
    restoredAnswers.proficiencyLevel = proficiencyLevel;
  }
  if (motivation && motivation in MOTIVATION_COPY) restoredAnswers.motivation = motivation;

  return restoredAnswers;
}

export function resolveInitialOnboardingState(
  savedState: string | null,
  search: string,
  now = Date.now()
): OnboardingState {
  const fallbackState: OnboardingState = {
    currentStep: 0,
    totalSteps: ONBOARDING_STEP_IDS.length,
    answers: {},
    startTime: now,
  };
  let restoredState = fallbackState;

  if (savedState) {
    try {
      const parsed = JSON.parse(savedState) as Partial<OnboardingState>;
      const savedStartTime = parsed.startTime;
      const isRecent =
        typeof savedStartTime === "number" &&
        now >= savedStartTime &&
        now - savedStartTime < ONBOARDING_STATE_MAX_AGE_MS;

      if (
        isRecent &&
        typeof savedStartTime === "number" &&
        parsed.answers &&
        typeof parsed.answers === "object"
      ) {
        restoredState = {
          currentStep:
            typeof parsed.currentStep === "number"
              ? Math.max(0, Math.min(parsed.currentStep, ONBOARDING_STEP_IDS.length - 1))
              : 0,
          totalSteps: ONBOARDING_STEP_IDS.length,
          answers: parsed.answers,
          startTime: savedStartTime,
        };
      }
    } catch {
      // A malformed saved session should not block a fresh onboarding visit.
    }
  }

  const searchParams = getSearchParams(search);
  if (isOnboardingDemoResume(searchParams) || isOnboardingFreeResume(searchParams)) {
    const restoredAnswers = restoreBoundedOnboardingAnswers(restoredState.answers, searchParams);

    return {
      ...restoredState,
      answers: restoredAnswers,
      currentStep: restoredAnswers.language
        ? ONBOARDING_STEP_IDS.indexOf(
            isOnboardingFreeResume(searchParams) ? "trial" : "speaking-demo"
          )
        : 0,
      totalSteps: ONBOARDING_STEP_IDS.length,
    };
  }

  if (!isOnboardingTrialResume(searchParams)) return restoredState;

  return {
    ...restoredState,
    currentStep: ONBOARDING_STEP_IDS.indexOf("trial"),
    totalSteps: ONBOARDING_STEP_IDS.length,
  };
}

export function buildOnboardingTrialAuthUrl(
  attribution: AttributionData | null,
  selection: OnboardingTrialSelection = {}
): string {
  const resumeParams = new URLSearchParams({ resume: "trial" });
  if (selection.packageType) resumeParams.set("plan", selection.packageType);
  if (selection.coupon?.trim()) resumeParams.set("coupon", selection.coupon.trim());
  if (selection.experimentVariant) {
    resumeParams.set(ONBOARDING_SIGNUP_TIMING_QUERY_KEY, selection.experimentVariant);
  }

  return buildOnboardingAuthUrl(attribution, resumeParams);
}

export function buildOnboardingDemoAuthUrl(
  attribution: AttributionData | null,
  answers: QuizAnswers,
  experimentVariant: OnboardingSignupTimingVariant
): string {
  const resumeParams = new URLSearchParams({
    resume: "demo",
    [ONBOARDING_SIGNUP_TIMING_QUERY_KEY]: experimentVariant,
  });
  if (answers.language) resumeParams.set("language", answers.language);
  if (answers.languageName) resumeParams.set("language_name", answers.languageName);
  if (answers.proficiencyLevel) resumeParams.set("level", answers.proficiencyLevel);
  if (answers.motivation) resumeParams.set("motivation", answers.motivation);

  return buildOnboardingAuthUrl(attribution, resumeParams);
}

export function buildOnboardingFreeAuthUrl(
  attribution: AttributionData | null,
  answers: QuizAnswers,
  experimentVariant?: OnboardingSignupTimingVariant | null
): string {
  const resumeParams = new URLSearchParams({ resume: "free" });
  if (answers.language) resumeParams.set("language", answers.language);
  if (answers.languageName) resumeParams.set("language_name", answers.languageName);
  if (answers.proficiencyLevel) resumeParams.set("level", answers.proficiencyLevel);
  if (answers.motivation) resumeParams.set("motivation", answers.motivation);
  if (experimentVariant) {
    resumeParams.set(ONBOARDING_SIGNUP_TIMING_QUERY_KEY, experimentVariant);
  }

  return buildOnboardingAuthUrl(attribution, resumeParams, "onboarding_free");
}

export function buildOnboardingFreePracticeUrl(
  answers: QuizAnswers,
  options: OnboardingFreePracticeOptions = {}
): string {
  const params = new URLSearchParams({
    onboarding_free: "true",
    sessionMode: "conversation",
  });
  if (answers.language) params.set("language", answers.language);
  const runtimeLevel = answers.proficiencyLevel
    ? ONBOARDING_LEVEL_TO_RUNTIME_LEVEL[answers.proficiencyLevel]
    : null;
  if (runtimeLevel) params.set("level", runtimeLevel);
  if (options.oauthSignup) params.set("oauth_signup", "true");

  return `/?${params.toString()}`;
}

function buildOnboardingAuthUrl(
  attribution: AttributionData | null,
  resumeParams: URLSearchParams,
  intent?: string
): string {
  const params = new URLSearchParams();
  params.set("redirect", `/onboarding?${resumeParams.toString()}`);
  if (intent) params.set("intent", intent);

  if (attribution?.utm_source) params.set("utm_source", attribution.utm_source);
  if (attribution?.utm_medium) params.set("utm_medium", attribution.utm_medium);
  if (attribution?.utm_campaign) params.set("utm_campaign", attribution.utm_campaign);
  if (attribution?.utm_term) params.set("utm_term", attribution.utm_term);
  if (attribution?.utm_content) params.set("utm_content", attribution.utm_content);
  if (attribution?.gclid) params.set("gclid", attribution.gclid);

  return `/auth?${params.toString()}`;
}

export function getOnboardingPlanSummary(
  answers: Pick<QuizAnswers, "motivation" | "proficiencyLevel">
): OnboardingPlanSummary {
  const motivation = answers.motivation ? MOTIVATION_COPY[answers.motivation] : undefined;

  return {
    motivationLabel: motivation?.label ?? "Everyday conversation",
    levelLabel:
      (answers.proficiencyLevel && LEVEL_COPY[answers.proficiencyLevel]) ?? "A comfortable start",
    firstChallenge:
      answers.proficiencyLevel === "A0"
        ? "Start with hello. Chicky will help with the next words."
        : (motivation?.challenge ?? "Say hello and tell Chicky what you want to practice."),
  };
}
